import React, { useMemo } from 'react';
import { useController } from 'react-hook-form';
import { v4 } from 'uuid';
import { isObjectEmpty } from 'common/utils/helpers';
import { VisibilityOffIcon, VisibilityOnIcon, InfoIcon } from 'components/icons';
import LoadingIndicator from 'components/shared/LoadingIndicator/LoadingIndicator';
import IconButton from 'components/shared/IconButton';
import { defaultColors } from '../../constants';
import styles from './DeviceChartLegend.module.css';

function DeviceChartLegend({
  htmlFor,
  datasets,
  actionsLoading,
  actionAnnotations,
  rebootAvailable,
  rebootVisible,
  onClickRebootVisible,
  editable
}) {
  const id = useMemo(() => v4(), []);

  return (
    <div className={styles.root} role="toolbar" aria-controls={htmlFor}>
      <div className={styles.list}>
        {datasets.map((dataset, index) => (
          <button type="button" key={`${dataset.label}_${index}`} className={styles.item} disabled={true}>
            <div className={styles.itemIcon}>
              <div
                style={{
                  display: 'inline-block',
                  height: '2px',
                  width: '24px',
                  backgroundColor: defaultColors[index]
                }}
              />
            </div>
            <div className={styles.itemText}>{dataset.label}</div>
          </button>
        ))}
        {rebootAvailable && (
          <div key="reboots" className={styles.item}>
            <div id={id} className={styles.itemText}>
              {actionsLoading ? (
                <LoadingIndicator text="Reboots - Loading" />
              ) : isObjectEmpty(actionAnnotations) ? (
                'Reboots - N/A'
              ) : (
                'Reboots'
              )}
            </div>
            {!actionsLoading &&
              (isObjectEmpty(actionAnnotations) ? (
                <IconButton
                  aria-labelledby={id}
                  variant="link"
                  color="secondary"
                  size="small"
                  data-tip="No reboot occurred during the specified time range"
                  className={styles.reboot}
                >
                  <InfoIcon />
                </IconButton>
              ) : editable ? (
                <RebootSwitchField aria-labelledby={id} />
              ) : (
                <RebootSwitch aria-labelledby={id} visible={rebootVisible} onClick={onClickRebootVisible} />
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DeviceChartLegend;

function RebootSwitchField(props) {
  const {
    field: { value, onChange }
  } = useController({ name: 'state.query.actions' });
  const index = value.indexOf('reboot');

  const handleClick = () => {
    if (index === -1) {
      onChange(value.concat('reboot'));
    } else {
      onChange([...value.slice(0, index), ...value.slice(index + 1, value.length)]);
    }
  };

  return <RebootSwitch visible={index !== -1} onClick={handleClick} {...props} />;
}

function RebootSwitch({ visible, ...rest }) {
  return (
    <IconButton
      variant="link"
      color="primary"
      size="small"
      data-tip={visible ? 'Hide reboot data' : 'Show reboot data'}
      className={styles.reboot}
      {...rest}
    >
      {visible ? <VisibilityOnIcon /> : <VisibilityOffIcon />}
    </IconButton>
  );
}
